/**********************************************************************
 *
 * Copyright (C) 2022 Almendo Technologies GmbH
 * Copyright (C) 2022 Marcus Harringer
 *
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of
 * Marcus Harringer. The intellectual and technical concepts contained
 * herein are proprietary to Marcus Harringer and may be covered
 * copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Marcus Harringer.
 *
 ***********************************************************************/
import { downloadData, remove, uploadData } from 'aws-amplify/storage';

//UUID
import { v4 as uuid } from 'uuid';

import { logError } from '../log/Logger';

// The stored file keys are paths without the access level folder, every file lies below public/
const getStoragePath = (fileKey) => 'public/' + fileKey;

/***************************************************************************
 * STORAGE (S3): file extension
 ***************************************************************************/
const getFileExtension = (file) => {

  try {
    if (file != null && file.name != null) {
      return file.name.split(".").pop();
    }
    return null;
  } catch (err) {
    return null;
  }

}

/***************************************************************************
 * STORAGE (S3): file key
 ***************************************************************************/
const getUserStorageFileKey = (user_id, file) => {

  let file_extension = getFileExtension(file);

  let file_key;

  if (file_extension != null && file_extension.length > 0) {
    file_key = uuid() + "." + file_extension;
  } else {
    file_key = uuid();
  }

  return {
    key: file_key,
    path: 'iot/v1/files/' + user_id + "/uploads/" + file_key //without 'public'
  }

}
/***************************************************************************
 * STORAGE (S3): options
 ***************************************************************************/
const getUserStorageUploadOptions = (user_id, file) => {

  return {
    metadata: {
      user_id: user_id,
      filename: encodeURIComponent(file.name), //S3 metadata headers only allow ASCII (umlauts break XHR)
      ts_unix_ms: file.lastModified.toString()
    },
    contentType: file.type,
    cacheControl: "no-cache",
    contentDisposition: "attachment"
  };

}



/***************************************************************************
 * STORAGE (S3): upload
 ***************************************************************************/
async function apiStorageUploadFile(user_id, file) {

  try {

    //TODO: upload to shares?

    const fileKey = getUserStorageFileKey(user_id, file);

    await uploadData({
      path: getStoragePath(fileKey.path),
      data: file,
      options: getUserStorageUploadOptions(user_id, file)
    }).result;

    return fileKey;

  } catch (err) {
    logError("apiStorageUploadFile", err);
    return null;
  }

}

/***************************************************************************
 * STORAGE (S3): delete
 ***************************************************************************/
async function apiStorageDeleteFile(fileKey) {

  try {

    //NOTE: shared files can also be deleted

    await remove({ path: getStoragePath(fileKey) });

  } catch (err) {
    logError("apiStorageDeleteFile", err);
  }


}

/***************************************************************************
 * STORAGE (S3): get
 ***************************************************************************/
async function apiStorageGetFile(fileKey) {

  try {

    //NOTE: shared files can be accessed

    const { body } = await downloadData({ path: getStoragePath(fileKey) }).result;

    // callers read the content as Blob from Body, like the download result of Amplify v4
    return { Body: await body.blob() };

  } catch (err) {
    logError("apiStorageGetFile", err);
  }

  return null;

}

/***************************************************************************
 * MODULE EXPORTES
 ***************************************************************************/
export {
  apiStorageUploadFile,
  apiStorageGetFile,
  apiStorageDeleteFile
}